import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { adminService } from "../../features/admin/service";
import { formatISTDateTime } from "../../utils/dateUtils";
import Spinner from "../../components/ui/Spinner";
import DateRangeFilter, { type DateFilterEntry } from "../../components/admin/DateRangeFilter";
import RefreshButton from "../../components/admin/RefreshButton";

const EVENT_LABELS: Record<string, string> = {
  contact_click: "Contacted seller",
  save_for_later: "Saved deal",
  unsave_for_later: "Unsaved deal",
  cross_sell_carousel_viewed: "Viewed cross-sell carousel",
  cross_sell_card_clicked: "Clicked cross-sell card",
  cross_sell_enquiry_sent: "Sent cross-sell enquiry",
};

const EVENT_COLORS: Record<string, string> = {
  contact_click: "bg-emerald-500/10 text-emerald-400",
  save_for_later: "bg-indigo-500/10 text-indigo-400",
  unsave_for_later: "bg-gray-500/10 text-gray-400",
  cross_sell_carousel_viewed: "bg-sky-500/10 text-sky-400",
  cross_sell_card_clicked: "bg-cyan-500/10 text-cyan-400",
  cross_sell_enquiry_sent: "bg-amber-500/10 text-amber-400",
};

const EVENT_TYPES = ["all", ...Object.keys(EVENT_LABELS)];
const ROLES = ["all", "buyer", "seller", "partner", "super_admin"];

const LOGIN_DATE_FIELDS = [
  { value: "last_login_at", label: "Last Login" },
  { value: "created_at", label: "Joined" },
];
const EVENT_DATE_FIELDS = [{ value: "created_at", label: "Event Time" }];

const PAGE_SIZE = 25;

const AdminActivityPage: React.FC = () => {
  const [logins, setLogins] = useState<any[]>([]);
  const [loginsTotal, setLoginsTotal] = useState(0);
  const [loginsPage, setLoginsPage] = useState(1);
  const [trend, setTrend] = useState<any[]>([]);
  const [breakdown, setBreakdown] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsPage, setEventsPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Recent Logins filters
  const [loginSearch, setLoginSearch] = useState("");
  const [loginRole, setLoginRole] = useState("all");
  const [loginDateFilters, setLoginDateFilters] = useState<DateFilterEntry[]>([]);

  // Recent Activity filters
  const [eventSearch, setEventSearch] = useState("");
  const [eventType, setEventType] = useState("all");
  const [eventDateFilters, setEventDateFilters] = useState<DateFilterEntry[]>([]);

  // Static widgets (trend + role breakdown) load once.
  const fetchOverview = useCallback(() => {
    return Promise.all([adminService.getLoginTrend(30), adminService.getRoleBreakdown()])
      .then(([t, b]) => {
        setTrend(t.trend);
        setBreakdown(b.breakdown);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // Recent Logins — refetches on any filter/page change.
  const fetchLogins = useCallback(() => {
    setLoading(true);
    const activeDates = loginDateFilters.filter((d) => d.from || d.to);
    return adminService
      .getRecentLogins({
        page: loginsPage,
        page_size: PAGE_SIZE,
        role: loginRole !== "all" ? loginRole : undefined,
        search: loginSearch || undefined,
        date_filters: activeDates.length ? JSON.stringify(activeDates) : undefined,
      })
      .then((r) => {
        setLogins(r.items);
        setLoginsTotal(r.total);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [loginsPage, loginRole, loginSearch, loginDateFilters]);

  useEffect(() => {
    fetchLogins();
  }, [fetchLogins]);

  // Recent Activity — refetches on any filter/page change.
  const fetchEvents = useCallback(() => {
    const activeDates = eventDateFilters.filter((d) => d.from || d.to);
    return adminService
      .getRecentEvents({
        page: eventsPage,
        page_size: PAGE_SIZE,
        event_type: eventType !== "all" ? eventType : undefined,
        search: eventSearch || undefined,
        date_filters: activeDates.length ? JSON.stringify(activeDates) : undefined,
      })
      .then((r) => {
        setEvents(r.items);
        setEventsTotal(r.total);
      })
      .catch(console.error);
  }, [eventsPage, eventType, eventSearch, eventDateFilters]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleRefreshAll = useCallback(() => {
    return Promise.all([fetchOverview(), fetchLogins(), fetchEvents()]).then(() => {});
  }, [fetchOverview, fetchLogins, fetchEvents]);

  const roleColor = (role: string) => {
    if (role === "seller") return "bg-cyan-500/10 text-cyan-400";
    if (role === "super_admin") return "bg-amber-500/10 text-amber-400";
    return "bg-emerald-500/10 text-emerald-400";
  };

  const loginsTotalPages = Math.max(1, Math.ceil(loginsTotal / PAGE_SIZE));
  const eventsTotalPages = Math.max(1, Math.ceil(eventsTotal / PAGE_SIZE));

  return (
    <div>
      <AdminPageHeader
        title="Activity"
        subtitle="Recent logins, user events, and breakdown"
        actions={<RefreshButton onRefresh={handleRefreshAll} />}
      />

      {/* Role breakdown */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {breakdown.map((b) => (
          <div key={b.role} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium mb-2 ${roleColor(b.role)}`}>{b.role}</span>
            <p className="text-2xl font-bold text-white">{b.count}</p>
          </div>
        ))}
      </div>

      {/* Login trend */}
      {trend.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-8">
          <h2 className="text-sm font-semibold text-gray-400 mb-4">Daily Logins — Last 30 Days</h2>
          <div className="flex items-end gap-1 h-20">
            {trend.map((p) => {
              const max = Math.max(...trend.map((x) => x.count), 1);
              const pct = Math.round((p.count / max) * 100);
              return (
                <div key={p.date} className="flex-1 flex flex-col items-center gap-1 group relative">
                  <div
                    style={{ height: `${pct}%` }}
                    className="w-full bg-indigo-500/40 rounded-t group-hover:bg-indigo-500/70 transition-colors min-h-[2px]"
                  />
                  <span className="absolute -top-5 text-xs text-indigo-300 opacity-0 group-hover:opacity-100 whitespace-nowrap">
                    {p.count}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-1 text-xs text-gray-600">
            <span>{trend[0]?.date}</span>
            <span>{trend[trend.length - 1]?.date}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Recent logins table */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-800">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-white">Recent Logins</h2>
              <span className="text-xs text-gray-500">{loginsTotal} users</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                placeholder="Search name, email, phone…"
                value={loginSearch}
                onChange={(e) => { setLoginSearch(e.target.value); setLoginsPage(1); }}
                className="flex-1 min-w-[160px] bg-gray-800 border border-gray-700 rounded-lg text-xs text-white placeholder-gray-500 px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
              <select
                value={loginRole}
                onChange={(e) => { setLoginRole(e.target.value); setLoginsPage(1); }}
                className="bg-gray-800 border border-gray-700 rounded-lg text-xs text-white px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {ROLES.map((r) => <option key={r} value={r}>{r === "all" ? "All roles" : r}</option>)}
              </select>
              <DateRangeFilter
                fields={LOGIN_DATE_FIELDS}
                value={loginDateFilters}
                onChange={(v) => { setLoginDateFilters(v); setLoginsPage(1); }}
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">User</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Role</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Last Login (IST)</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4} className="py-16 text-center text-gray-500"><Spinner /></td></tr>
                ) : logins.length === 0 ? (
                  <tr><td colSpan={4} className="py-16 text-center text-gray-500">No login data yet</td></tr>
                ) : (
                  logins.map((u) => (
                    <tr key={u.id} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400 font-semibold text-xs flex-shrink-0">
                            {u.name?.[0]?.toUpperCase() || "?"}
                          </div>
                          <div>
                            <p className="text-white font-medium text-sm">{u.name || "—"}</p>
                            <p className="text-gray-500 text-xs">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${roleColor(u.role)}`}>{u.role}</span>
                      </td>
                      <td className="py-3 px-4 text-gray-400 text-xs">
                        {u.last_login_at ? formatISTDateTime(u.last_login_at) : "—"}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 text-xs ${u.is_active !== false ? "text-green-400" : "text-red-400"}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${u.is_active !== false ? "bg-green-400" : "bg-red-400"}`} />
                          {u.is_active !== false ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {loginsTotalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-800 text-xs text-gray-500">
              <span>Page {loginsPage} of {loginsTotalPages}</span>
              <div className="flex gap-2">
                <button
                  disabled={loginsPage <= 1}
                  onClick={() => setLoginsPage((p) => Math.max(1, p - 1))}
                  className="px-2.5 py-1 rounded-lg bg-gray-800 border border-gray-700 disabled:opacity-40 hover:bg-gray-700"
                >
                  Prev
                </button>
                <button
                  disabled={loginsPage >= loginsTotalPages}
                  onClick={() => setLoginsPage((p) => Math.min(loginsTotalPages, p + 1))}
                  className="px-2.5 py-1 rounded-lg bg-gray-800 border border-gray-700 disabled:opacity-40 hover:bg-gray-700"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Recent activity feed */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-800">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-white">Recent Activity</h2>
              <span className="text-xs text-gray-500">{eventsTotal} events</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                placeholder="Search buyer, seller, item…"
                value={eventSearch}
                onChange={(e) => { setEventSearch(e.target.value); setEventsPage(1); }}
                className="flex-1 min-w-[160px] bg-gray-800 border border-gray-700 rounded-lg text-xs text-white placeholder-gray-500 px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
              <select
                value={eventType}
                onChange={(e) => { setEventType(e.target.value); setEventsPage(1); }}
                className="bg-gray-800 border border-gray-700 rounded-lg text-xs text-white px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t}>{t === "all" ? "All actions" : (EVENT_LABELS[t] ?? t)}</option>
                ))}
              </select>
              <DateRangeFilter
                fields={EVENT_DATE_FIELDS}
                value={eventDateFilters}
                onChange={(v) => { setEventDateFilters(v); setEventsPage(1); }}
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-[520px]">
            {events.length === 0 ? (
              <p className="py-16 text-center text-gray-500 text-sm">No activity yet</p>
            ) : (
              <ul className="divide-y divide-gray-800/50">
                {events.map((ev) => (
                  <li key={ev.id} className="px-5 py-3 hover:bg-gray-800/30 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${EVENT_COLORS[ev.event_type] ?? "bg-gray-500/10 text-gray-400"}`}>
                            {EVENT_LABELS[ev.event_type] ?? ev.event_type}
                          </span>
                          {ev.channel && (
                            <span className="text-xs text-gray-500">{ev.channel}</span>
                          )}
                        </div>
                        {ev.item_title && (
                          <p className="text-white text-sm mt-1 truncate">{ev.item_title}</p>
                        )}
                        {ev.seller_name && (
                          <p className="text-gray-500 text-xs mt-0.5">Seller: {ev.seller_name}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          {ev.buyer ? (
                            <p className="text-gray-400 text-xs">
                              {ev.buyer.name || ev.buyer.email}
                              {ev.buyer.name && ev.buyer.email && (
                                <span className="text-gray-600"> · {ev.buyer.email}</span>
                              )}
                            </p>
                          ) : (
                            <p className="text-gray-600 text-xs">Unknown user</p>
                          )}
                          {ev.source && (
                            <span className="text-gray-600 text-xs">· {ev.source}</span>
                          )}
                        </div>
                      </div>
                      <span className="text-gray-600 text-xs whitespace-nowrap flex-shrink-0">
                        {ev.created_at ? formatISTDateTime(ev.created_at) : "—"}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {eventsTotalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-800 text-xs text-gray-500">
              <span>Page {eventsPage} of {eventsTotalPages}</span>
              <div className="flex gap-2">
                <button
                  disabled={eventsPage <= 1}
                  onClick={() => setEventsPage((p) => Math.max(1, p - 1))}
                  className="px-2.5 py-1 rounded-lg bg-gray-800 border border-gray-700 disabled:opacity-40 hover:bg-gray-700"
                >
                  Prev
                </button>
                <button
                  disabled={eventsPage >= eventsTotalPages}
                  onClick={() => setEventsPage((p) => Math.min(eventsTotalPages, p + 1))}
                  className="px-2.5 py-1 rounded-lg bg-gray-800 border border-gray-700 disabled:opacity-40 hover:bg-gray-700"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminActivityPage;
